import {
  EAgentToolType,
  IActionFolder,
  EDatasourceType,
  EUserAccessPart,
  isInlineCodeDatasourceType,
  IActionDefinitionDto,
  IDatabaseTableModel,
  IDataDiagramSchemeDto,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import { IRestApiEndpoint } from "@kuika/kuika-cl-model/src/dtos/datasource.dto";
import { AxiosResponse } from "axios";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { DataDiagramSchemeService } from "../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../services/datasource-service";
import { OfflineTablesService } from "../../../services/offline-tables-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/**
 * Row actions for a data source, a table or an action — the same work the left tree's three-dot
 * menus do, as functions rather than methods of a 4000-line panel, so the landing page's own
 * menu runs the flows instead of a second implementation of them.
 *
 * Only the flows live here. Each surface keeps its own UI state (which row's delete modal is
 * open) and passes the result back in, the way `screen-row-actions.tsx` does for screens.
 */

/**
 * What the project role lets this user change, per kind of row. Every surface that offers a
 * row's actions asks the same three questions — the datasources list, the AI hub, and the
 * switchers on each editor's bar — so the answer lives here, once.
 */
export const canManageDatasources = (sd: IScreenDesignContextState): boolean =>
  KMainFunctions.hasUserAccessInWorkspaceByProjectRole(EUserAccessPart.ManageDatasource, sd?.project?.projectAccess);

export const canManageTables = (sd: IScreenDesignContextState): boolean =>
  KMainFunctions.hasUserAccessInWorkspaceByProjectRole(EUserAccessPart.ManageDataTable, sd?.project?.projectAccess);

export const canManageActions = (sd: IScreenDesignContextState): boolean =>
  KMainFunctions.hasUserAccessInWorkspaceByProjectRole(EUserAccessPart.ManageCustomActions, sd?.project?.projectAccess);

/**
 * Datasource types whose ACTIONS are not individually editable, so their rows carry no menu:
 * a REST or OData collection edits its endpoints through the datasource panel, C# and the
 * inline-code runtimes hold their actions inside one source file, and MQTT/Bluetooth actions
 * are generated from the connection. The tree gates its action menu on exactly this list.
 */
const ACTION_MENULESS_DATASOURCE_TYPES: EDatasourceType[] = [
  EDatasourceType.RestApi,
  EDatasourceType.CSharpFunctions,
  EDatasourceType.CustomAuthenticationProvider,
  EDatasourceType.CustomAuthorizationProvider,
  EDatasourceType.CustomVerificationCodeSenderProvider,
  EDatasourceType.CustomConnectionStringProvider,
  EDatasourceType.CustomExternalTokenValidatorProvider,
  EDatasourceType.CustomMailProvider,
  EDatasourceType.Mqtt,
  EDatasourceType.BleService,
  EDatasourceType.OData
];

/** Whether an action row offers any menu at all. */
export const hasActionRowMenu = (datasource: IDatasourceDto): boolean =>
  !ACTION_MENULESS_DATASOURCE_TYPES.includes(datasource.datasourceType) &&
  !isInlineCodeDatasourceType(datasource.datasourceType);

/**
 * A template-based AI action has no editor of its own — its behaviour comes from the template —
 * so the tree hides Edit on it. `getStringifiedAiActionInfo` is the provider's own reader.
 */
export const canEditAction = (sd: IScreenDesignContextState, action: IActionDefinitionDto): boolean =>
  !sd.getStringifiedAiActionInfo?.(action.aiActionInfo)?.templateId;

/** The project's own database, in any of its three engines. */
export const isManagedDbType = (datasource: IDatasourceDto): boolean =>
  datasource.datasourceType === EDatasourceType.ManagedDb ||
  datasource.datasourceType === EDatasourceType.ManagedDbOracle ||
  datasource.datasourceType === EDatasourceType.ManagedDbPostgre;

/**
 * Editing a datasource means opening its connection panel; AI Actions and Blockly do not have
 * one (their datasource is created and then only its actions are edited), and neither does the
 * managed database — the project owns its connection, and the centre has no editor for it.
 */
export const canEditDatasource = (datasource: IDatasourceDto): boolean =>
  !isManagedDbType(datasource) &&
  datasource.datasourceType !== EDatasourceType.AIActions &&
  datasource.datasourceType !== EDatasourceType.Blockly;

/** Browse Data writes through a managed-DB-only endpoint. */
export const canBrowseTableRows = (datasource: IDatasourceDto): boolean => isManagedDbType(datasource);

/**
 * A table's own editor is only offered on a managed database: an external server's columns are
 * read from its schema, not authored here. (`connectionString === ""` is the managed test the
 * tree uses; the schema check on top of it decides whether the editor opens read-only.)
 */
export const canEditTable = (datasource: IDatasourceDto): boolean => datasource.connectionString === "";

/** Connected SQL servers can re-read their schema; the managed database has nothing to re-read. */
export const canRefreshDatasource = (datasource: IDatasourceDto): boolean =>
  datasource.datasourceType === EDatasourceType.MsSql ||
  datasource.datasourceType === EDatasourceType.Oracle ||
  datasource.datasourceType === EDatasourceType.Postgre ||
  datasource.datasourceType === EDatasourceType.CustomDb;

/**
 * A table is editable when it belongs to the project's own schema. Tables from another schema
 * are visible — the diagram reads them — but renaming or dropping one would be a change to
 * somebody else's database.
 */
export const isTableEditable = (sd: IScreenDesignContextState, table: IDatabaseTableModel): boolean => {
  // The project list arrives from the server and can hold a row before its id does. Without an id
  // there is no schema to compare with — and two missing ids must not match the wrong project's.
  const currentProjectId: string | undefined = sd?.project?.id?.toLowerCase();
  if (!currentProjectId) return false;
  const projectSchemaName: string | undefined = sd.projects?.find(
    (project): boolean => project.id?.toLowerCase() === currentProjectId
  )?.schemaName;
  return (table.schema || "dbo") === projectSchemaName;
};

//  ────────────────────────────────────────────────────────────────────────────
//  Swagger-less REST endpoints
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The endpoint a Swagger-less REST action was generated from, by the name the generator gives it
 * (`<operation>From<datasource>`, compared without case). Such an action IS its endpoint: it is
 * edited in the datasource's endpoint panel and deleted together with the endpoint. Undefined for
 * any other datasource type, and for an action no endpoint produced.
 */
export const findRestEndpointIndex = (datasource: IDatasourceDto, action: IActionDefinitionDto): number | undefined => {
  if (datasource.datasourceType !== EDatasourceType.RestApiWOSwagger) return undefined;
  const actionName: string = (action.name ?? "").toLowerCase();
  const index: number = (datasource.restApiConnectionRequest?.paths ?? []).findIndex(
    (path: IRestApiEndpoint): boolean =>
      `${path.operations?.[0]?.operationName}From${datasource.name}`.toLowerCase() === actionName
  );
  return index === -1 ? undefined : index;
};

/** What the delete dialog calls an endpoint: its operation, or the datasource's when it has none. */
export const getRestEndpointName = (datasource: IDatasourceDto, index: number): string =>
  datasource.restApiConnectionRequest?.paths?.[index]?.operations?.[0]?.operationName ??
  `${datasource.name}'s Endpoint`;

/**
 * Deletes a Swagger-less REST action the way the Sources tree does: the action, then its endpoint
 * out of the datasource's paths, saved. Deleting only the action left the endpoint behind in the
 * connection with no action for it. The save carries a copy — the provider's datasource is not
 * edited in place.
 */
export const deleteRestEndpointFromRow = async (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto,
  action: IActionDefinitionDto,
  endpointIndex: number
): Promise<void> => {
  try {
    const deleted: AxiosResponse | undefined = await DatasourceService.DeleteAction(action);
    if (!deleted || deleted.status !== 200) throw new Error("The Rest API endpoint's action could not be deleted.");

    const paths: IRestApiEndpoint[] = datasource.restApiConnectionRequest?.paths ?? [];
    if (paths.length === 0) throw new Error("Rest API Datasource Endpoints are not found");
    const saved: AxiosResponse | undefined = await DatasourceService.SaveDatasource({
      ...datasource,
      restApiConnectionRequest: {
        ...datasource.restApiConnectionRequest,
        paths: paths.filter((_path: IRestApiEndpoint, index: number): boolean => index !== endpointIndex)
      }
    });
    if (!saved || saved.status !== 200) throw new Error("Something went wrong while deleting the Rest API Endpoint");

    if (
      sd.activeEditingDatasource?.id === datasource.id &&
      sd.activeEditingDatasourceIndex !== undefined &&
      sd.activeEditingDatasourceIndex === endpointIndex
    ) {
      sd.cancelCreatingOrEditingDatasource();
    }
    sd.loadActionDefinitions();
    sd.refreshActions();
    KMainFunctions.displaySuccessNotification("Rest API Endpoint is deleted successfully.");
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
  }
};

//  ────────────────────────────────────────────────────────────────────────────
//  AI agents that use an action
//  ────────────────────────────────────────────────────────────────────────────

/** The part of an agent tool this reads out of `aiAgentInfo`. */
interface IAgentToolReference {
  toolType?: EAgentToolType;
  actionId?: string;
}

interface IAgentToolHolder {
  tools?: IAgentToolReference[];
}

/** The part of `aiAgentInfo` this reads: the crew and the manager, each with its tools. */
interface IAgentInfoToolReferences {
  crewAgents?: IAgentToolHolder[];
  managerAgent?: IAgentToolHolder;
}

const usesAction = (holder: IAgentToolHolder | undefined, actionId: string): boolean =>
  Array.isArray(holder?.tools) &&
  holder.tools.some(
    (tool: IAgentToolReference): boolean => tool?.toolType === EAgentToolType.KAction && tool.actionId === actionId
  );

/**
 * The AI agents that call the action as a tool, by name, once each — the warning a delete shows,
 * since deleting the action leaves those agents with a broken reference. An agent whose info does
 * not parse is skipped rather than failing the delete.
 */
export const findAgentUsagesOfAction = (actions: IActionDefinitionDto[], actionId: string): string[] =>
  (actions ?? [])
    .filter((candidate: IActionDefinitionDto): boolean => candidate.actionType === EDatasourceType.AIAgent)
    .filter((agent: IActionDefinitionDto): boolean => {
      if (!agent.aiAgentInfo) return false;
      try {
        const parsed: unknown = JSON.parse(agent.aiAgentInfo);
        if (!parsed || typeof parsed !== "object") return false;
        const info: IAgentInfoToolReferences = parsed as IAgentInfoToolReferences;
        const crew: IAgentToolHolder[] = Array.isArray(info.crewAgents) ? info.crewAgents : [];
        return (
          crew.some((member: IAgentToolHolder): boolean => usesAction(member, actionId)) ||
          usesAction(info.managerAgent, actionId)
        );
      } catch {
        return false;
      }
    })
    .map((agent: IActionDefinitionDto): string => agent.name);

export const refreshThirdPartyDatasource = async (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto
): Promise<void> => {
  try {
    const response = await DatasourceService.GetSchema(datasource.id, false, false);
    sd.refresh3rdPartyDsSchema(datasource.id, response.data.tables);
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
  }
};

/** Closes the editor first when the deleted datasource is the one open in it. */
export const deleteDatasourceFromRow = async (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto
): Promise<void> => {
  try {
    const res = await DatasourceService.DeleteDatasource(datasource.id);
    if (res && res.status === 200) {
      if (sd.activeEditingDatasource?.id === datasource.id) {
        sd.cancelCreatingOrEditingDatasource();
      }
      sd.refreshDatabases();
      sd.refreshActions();
      KMainFunctions.displaySuccessNotification("Datasource deleted successfully.");
    }
  } catch (error) {
    KMainFunctions.displayErrorNotification(error);
  }
};

/**
 * Dropping a table is a migration, not a delete call: the schema service takes the table marked
 * `dropped`, and an offline mirror of it has to go in the same breath or the generated app keeps
 * syncing a table that no longer exists.
 */
export const deleteTableFromRow = async (sd: IScreenDesignContextState, table: IDatabaseTableModel): Promise<void> => {
  try {
    const offlineTable = sd.offlineTables?.find((item): boolean => item.tableName === table.name);
    const tableObj = {} as IDatabaseTableModel;
    tableObj.id = table.id;
    tableObj.name = table.name;
    tableObj.schema = table.schema;
    tableObj.dropped = true;
    tableObj.columns = [];

    const final = {} as IDataDiagramSchemeDto;
    final.tables = [tableObj];
    const results = await Promise.allSettled([
      DataDiagramSchemeService.ExecuteMigrationScript(final),
      offlineTable ? OfflineTablesService.delete(table.name) : Promise.resolve({ status: 200 })
    ]);
    if (results[0].status === "rejected") throw results[0].reason;
    if (results[1].status === "rejected") throw results[1].reason;

    const migration = results[0].value as { status: number };
    if (migration.status === 200) {
      if (sd.editingTable?.id === table.id) {
        sd.cancelCreatingOrEditingDatasource();
      }
      sd.refreshTables();
      sd.updateDatabaseDiagramData();
      KMainFunctions.displaySuccessNotification("Table is deleted successfully");
    }
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
  }
};

export const deleteActionFromRow = async (
  sd: IScreenDesignContextState,
  action: IActionDefinitionDto
): Promise<void> => {
  try {
    const res = await DatasourceService.DeleteAction(action);
    if (res && res.status === 200) {
      if (sd.editingAction?.id === action.id) {
        sd.cancelCreatingOrEditingDatasource();
      }
      sd.loadActionDefinitions();
      sd.refreshActions();
      KMainFunctions.displaySuccessNotification("Action deleted successfully");
    }
  } catch (error) {
    KMainFunctions.displayErrorNotification(error);
  }
};

/**
 * Takes an action out of whichever folder holds it, top level or nested — "Move out of folder"
 * on an AI function row. An action lives in at most one folder, so there is one to find.
 */
export const removeActionFromItsFolder = (sd: IScreenDesignContextState, actionId: string): void => {
  const folders: IActionFolder[] = sd.project?.actionFolders || [];
  const current: IActionFolder | undefined = sd.findFolderByActionId(actionId, folders);
  if (!current) return;
  if (current.parentFolderId) {
    sd.removeActionFromFolderInFolder(current.parentFolderId, current.id, actionId);
  } else {
    sd.removeActionFromFolder(current.id, actionId);
  }
};

// ── The Actions group's "+" ─────────────────────────────────────────────────────────────────
// What the Sources tree's group "+" menus offer on a datasource (`datasourceActionsMenuOptions`),
// as predicates, so the Datasources landing's group rows offer the same entries on the same types.

/**
 * Types whose Actions group creates no action of its own: REST and C# datasources make theirs
 * elsewhere (a Swagger-less REST datasource makes endpoints instead), MQTT/Bluetooth actions are
 * generated from the connection, and OData reads its collection.
 */
const ACTION_CREATELESS_DATASOURCE_TYPES: EDatasourceType[] = [
  EDatasourceType.RestApi,
  EDatasourceType.RestApiWOSwagger,
  EDatasourceType.CSharpFunctions,
  EDatasourceType.CustomAuthenticationProvider,
  EDatasourceType.CustomAuthorizationProvider,
  EDatasourceType.CustomVerificationCodeSenderProvider,
  EDatasourceType.CustomConnectionStringProvider,
  EDatasourceType.CustomExternalTokenValidatorProvider,
  EDatasourceType.CustomMailProvider,
  EDatasourceType.BleService,
  EDatasourceType.Mqtt,
  EDatasourceType.OData
];

/** The Sources tree offers no "+" on an MQTT datasource's Actions at all. */
export const hasActionsGroupMenu = (datasource: IDatasourceDto): boolean =>
  datasource.datasourceType !== EDatasourceType.Mqtt;

/** "New … Action" — the datasource's own kind of action, opened in its editor pre-named. */
export const canCreateActionIn = (datasource: IDatasourceDto): boolean =>
  !ACTION_CREATELESS_DATASOURCE_TYPES.includes(datasource.datasourceType) &&
  !isInlineCodeDatasourceType(datasource.datasourceType);

/** The word "New … Action" carries for the datasource: its own kind, SQL otherwise. */
export const getNewActionKindLabel = (datasource: IDatasourceDto): string => {
  if (datasource.datasourceType === EDatasourceType.Blockly) return "Blockly";
  if (datasource.datasourceType === EDatasourceType.JavaScriptAction) return "TypeScript";
  if (datasource.datasourceType === EDatasourceType.SAP) return "SAP";
  return "SQL";
};

/** "New Select Action" — the query builder's generic select, which Blockly and SAP have no use for. */
export const canCreateSelectAction = (datasource: IDatasourceDto): boolean =>
  canCreateActionIn(datasource) &&
  datasource.datasourceType !== EDatasourceType.Blockly &&
  datasource.datasourceType !== EDatasourceType.SAP;

/** "New Rest API Endpoint" — a Swagger-less REST datasource grows by endpoints. */
export const canCreateRestEndpoint = (datasource: IDatasourceDto): boolean =>
  datasource.datasourceType === EDatasourceType.RestApiWOSwagger;

/** "New Folder" under Actions: every type but Blockly and the inline-code runtimes. */
export const canCreateActionFolder = (datasource: IDatasourceDto): boolean =>
  datasource.datasourceType !== EDatasourceType.Blockly && !isInlineCodeDatasourceType(datasource.datasourceType);

/**
 * "New … Action" on an action folder: where the Actions group creates one AND the type has
 * folders. A folder of a REST, MQTT or Bluetooth datasource used to offer a SQL action its group
 * never did.
 */
export const canCreateActionInFolder = (datasource: IDatasourceDto): boolean =>
  canCreateActionIn(datasource) && canCreateActionFolder(datasource);
